import React, { useEffect, useState } from 'react';
import { Modal, Form, Select, Button, Typography, Input, Switch } from 'antd';
import NegativeProposalReasonService, { INegativeProposalReason } from '../../services/NegativeProposalReasonService';
import { normalizeTurkish, openNotification } from '../../utils';

const { Text } = Typography;

const sortReasons = (reasons: INegativeProposalReason[]) => {
  return [...reasons].sort((a, b) => {
    if (a.isOther !== b.isOther) {
      return Number(a.isOther) - Number(b.isOther);
    }
    return (a.text || '').localeCompare(b.text || '', 'tr-TR');
  });
};

interface NegativeProposalReasonModalProps {
  open: boolean;
  onCancel: () => void;
  onSubmit: (values: {
    negativeReasonId?: number;
    negativeReasonText?: string;
    sendNegativeSurvey?: boolean;
  }) => Promise<void>;
  confirmLoading?: boolean;
  title?: string;
  submitText?: string;
  initialValues?: NegativeProposalReasonFormValues;
  showSurveySwitch?: boolean;
}

interface NegativeProposalReasonFormValues {
  negativeReasonId?: number;
  negativeReasonText?: string;
  sendNegativeSurvey?: boolean;
}

const NegativeProposalReasonModal: React.FC<NegativeProposalReasonModalProps> = ({
  open,
  onCancel,
  onSubmit,
  confirmLoading = false,
  title = 'Teklifin Olumsuz Sonuçlanma Nedeni',
  submitText = 'Olumsuz Olarak Sonlandır',
  initialValues,
  showSurveySwitch = true,
}) => {
  const [form] = Form.useForm<NegativeProposalReasonFormValues>();
  const [reasons, setReasons] = useState<INegativeProposalReason[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedReason, setSelectedReason] = useState<INegativeProposalReason | null>(null);
  const sendNegativeSurvey = Form.useWatch('sendNegativeSurvey', form);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setReasons([]);
      setSelectedReason(null);
      return;
    }

    setLoading(true);
    NegativeProposalReasonService.listAll({ 'isActive.equals': true })
      .then((response) => setReasons(sortReasons(response.data)))
      .catch((error) => {
        console.error('Failed to load negative reasons', error);
        openNotification('error', 'Hata', 'Olumsuz teklif nedenleri yüklenirken hata oluştu.');
      })
      .finally(() => setLoading(false));
  }, [open, form]);

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      negativeReasonId: initialValues?.negativeReasonId,
      negativeReasonText: initialValues?.negativeReasonText,
      sendNegativeSurvey: initialValues?.sendNegativeSurvey ?? false,
    });
    if (initialValues?.negativeReasonId) {
      const reason = reasons.find((item) => item.id === initialValues.negativeReasonId) ?? null;
      setSelectedReason(reason);
      return;
    }
    setSelectedReason(null);
  }, [open, form, reasons, initialValues?.negativeReasonId, initialValues?.negativeReasonText, initialValues?.sendNegativeSurvey]);

  useEffect(() => {
    if (!open || !sendNegativeSurvey) {
      return;
    }
    form.setFieldsValue({
      negativeReasonId: undefined,
      negativeReasonText: undefined,
    });
    setSelectedReason(null);
  }, [open, sendNegativeSurvey, form]);

  const handleReasonChange = (value?: number) => {
    const reason = reasons.find((item) => item.id === value) ?? null;
    setSelectedReason(reason);
    if (!reason || !reason.isOther) {
      form.setFieldsValue({ negativeReasonText: undefined });
    }
  };

  const handleFinish = async (values: NegativeProposalReasonFormValues) => {
    await onSubmit({
      negativeReasonId: values.negativeReasonId,
      negativeReasonText: selectedReason?.isOther && !values.sendNegativeSurvey
        ? values.negativeReasonText?.trim()
        : undefined,
      sendNegativeSurvey: values.sendNegativeSurvey ?? false,
    });
  };

  const handleSurveySwitchChange = (checked: boolean) => {
    form.setFieldValue('sendNegativeSurvey', checked);
    if (checked) {
      form.setFieldsValue({
        negativeReasonId: undefined,
        negativeReasonText: undefined,
      });
      setSelectedReason(null);
    }
  };

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      footer={null}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        {showSurveySwitch && (
          <Form.Item name="sendNegativeSurvey" valuePropName="checked" label="Anket Gönder">
            <Switch checkedChildren="Açık" unCheckedChildren="Kapalı" onChange={handleSurveySwitchChange} />
          </Form.Item>
        )}
        <Form.Item
          name="negativeReasonId"
          label="Teklifin Olumsuz Sonuçlanma Gerekçesi"
          required={!sendNegativeSurvey}
          dependencies={['sendNegativeSurvey']}
          rules={[
            {
              validator: (_, value) => {
                if (!sendNegativeSurvey && !value) {
                  return Promise.reject(new Error('Lütfen bir neden seçin.'));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Select
            showSearch
            allowClear
            disabled={Boolean(sendNegativeSurvey)}
            placeholder="Olumsuzluk gerekçesini seçiniz"
            loading={loading}
            optionFilterProp="label"
            filterOption={(input, option) =>
              normalizeTurkish(option?.label as string).includes(normalizeTurkish(input))
            }
            options={reasons.map((reason) => ({
              label: reason.text,
              value: reason.id,
            }))}
            onChange={handleReasonChange}
          />
        </Form.Item>
        {selectedReason?.isOther && !sendNegativeSurvey && (
          <Form.Item
            name="negativeReasonText"
            label="Açıklama"
            rules={[{ required: true, message: 'Lütfen diğer neden için açıklama girin.' }]}
          >
            <Input.TextArea rows={4} placeholder="Olumsuz sonuçlanma gerekçesini kısaca belirtiniz" />
          </Form.Item>
        )}
        <Form.Item>
          <Button type="primary" htmlType="submit" block loading={confirmLoading}>
            {submitText}
          </Button>
        </Form.Item>
        {!loading && reasons.length === 0 && (
          <Text type="secondary">Aktif özel neden bulunamadı.</Text>
        )}
      </Form>
    </Modal>
  );
};

export default NegativeProposalReasonModal;
